/**
 * Geometry for the admin-configurable Snakes & Ladders board.
 *
 * The theme stores `board_cols` and `board_rows` (each 4–7, max 7×7).
 * Square count, snake/ladder placement and decorations are all derived from
 * that rectangle, so the game and the admin preview always agree and every
 * cell is a real playable square.
 */

export const BOARD_DIM_OPTIONS = [4, 5, 6, 7];
export const DEFAULT_BOARD_COLS = 5;
export const DEFAULT_BOARD_ROWS = 5;
export const MAX_BOARD_DIM = 7;

/** Older themes only had a square count — map those onto a full rectangle ≤ 7×7. */
const LEGACY_SIZE_TO_DIMS = {
  16: [4, 4],
  20: [5, 4],
  24: [6, 4],
  25: [5, 5],
  28: [7, 4],
  30: [6, 5],
  35: [7, 5],
  36: [6, 6],
  42: [7, 6],
  49: [7, 7],
  40: [7, 6],
  45: [7, 7],
  50: [7, 7],
};

export function normalizeBoardDim(value, fallback = DEFAULT_BOARD_COLS) {
  const n = parseInt(value, 10);
  return BOARD_DIM_OPTIONS.includes(n) ? n : fallback;
}

/**
 * Resolve columns/rows from theme fields. Prefers board_cols / board_rows;
 * falls back to a legacy board_size mapping, then to 5×5.
 */
export function resolveBoardDims(source) {
  if (source == null || typeof source !== "object") {
    return { cols: DEFAULT_BOARD_COLS, rows: DEFAULT_BOARD_ROWS };
  }

  const hasCols = source.board_cols != null || source.cols != null;
  const hasRows = source.board_rows != null || source.rows != null;
  if (hasCols || hasRows) {
    return {
      cols: normalizeBoardDim(source.board_cols ?? source.cols, DEFAULT_BOARD_COLS),
      rows: normalizeBoardDim(source.board_rows ?? source.rows, DEFAULT_BOARD_ROWS),
    };
  }

  const legacy = parseInt(source.board_size ?? source.size, 10);
  if (Number.isFinite(legacy) && LEGACY_SIZE_TO_DIMS[legacy]) {
    const [cols, rows] = LEGACY_SIZE_TO_DIMS[legacy];
    return { cols, rows };
  }

  return { cols: DEFAULT_BOARD_COLS, rows: DEFAULT_BOARD_ROWS };
}

/** One snake and one ladder per five squares, matching the legacy 5 + 5 on a 25 board. */
export function linksPerKind(size) {
  const n = Math.max(16, parseInt(size, 10) || 25);
  return Math.max(2, Math.round(n / 5));
}

/** Numbering runs back and forth (boustrophedon) with row 1 at the bottom. */
function rcOf(square, cols) {
  const idx = square - 1;
  const row = Math.floor(idx / cols) + 1;
  const offset = idx % cols;
  return [row, row % 2 === 1 ? offset + 1 : cols - offset];
}

function squareAt(row, col, cols) {
  const offset = row % 2 === 1 ? col - 1 : cols - col;
  return (row - 1) * cols + offset + 1;
}

/** Evenly spaced heads, so the rigged dice never needs more than six pips to reach the next one. */
function headSquares(size, count) {
  const step = (size - 2) / (count + 1);
  const heads = [];
  let prev = 1;
  for (let i = 1; i <= count; i += 1) {
    const candidate = Math.max(prev + 1, 1 + Math.round(step * i));
    if (candidate > size - 1) break;
    heads.push(candidate);
    prev = candidate;
  }
  return heads;
}

/**
 * Row/column steps a link may take, in order of preference. Row travel is never
 * allowed to be smaller than column travel: a one-row, two-column hop draws a
 * 27° line, which makes the two ends look like they sit in the same row.
 * Keeping the slope at 45° or steeper is what makes a climb or drop obvious.
 */
const LADDER_STEPS = [
  [1, 1],
  [1, 0],
  [2, 1],
  [2, 0],
  [2, 2],
];
const SNAKE_STEPS = [
  [2, 1],
  [2, 0],
  [1, 1],
  [1, 0],
  [2, 2],
];
/** Longer reaches, only used when a crowded board leaves nothing closer free. */
const RELAXED_STEPS = [
  [3, 1],
  [3, 0],
  [3, 2],
  [4, 1],
  [4, 2],
  [3, 3],
];

/** Grid cells the drawn artwork will pass through on its way between squares. */
function pathCells(fromRow, fromCol, toRow, toCol) {
  const cells = new Set();
  const steps = 10;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const row = Math.round(fromRow + (toRow - fromRow) * t);
    const col = Math.round(fromCol + (toCol - fromCol) * t);
    cells.add(`${row},${col}`);
  }
  return cells;
}

function pickDest(head, kind, board, relaxed = false) {
  const { size, cols, rows, taken, covered } = board;
  const [headRow, headCol] = rcOf(head, cols);
  const base = kind === "ladder" ? LADDER_STEPS : SNAKE_STEPS;
  const steps = relaxed ? [...base, ...RELAXED_STEPS] : base;
  // Alternate the lean so neighbouring links do not stack on the same diagonal.
  const dirs = head % 2 === 0 ? [1, -1] : [-1, 1];
  // Rows snake back and forth, so the square directly above a head can be its
  // own neighbour. A climb or drop of one or two squares is not worth a
  // question, so insist every link actually moves the player.
  const minJump = Math.max(3, cols - 1);
  let fallback = null;

  for (const [rowStep, colStep] of steps) {
    const row = kind === "ladder" ? headRow + rowStep : headRow - rowStep;
    if (row < 1 || row > rows) continue;
    // A straight-up link has only one destination, not one per direction.
    for (const dir of colStep === 0 ? [1] : dirs) {
      const col = headCol + dir * colStep;
      if (col < 1 || col > cols) continue;
      const square = squareAt(row, col, cols);
      // A whole row separates them, so direction along the path is guaranteed.
      if (square < 2 || square >= size) continue;
      if (Math.abs(square - head) < minJump) continue;
      if (taken.has(square)) continue;

      // Prefer a route that does not run over artwork already on the board.
      const cells = pathCells(headRow, headCol, row, col);
      cells.delete(`${headRow},${headCol}`);
      if (![...cells].some((cell) => covered.has(cell))) return square;
      if (fallback == null) fallback = square;
    }
  }
  return fallback;
}

function buildLinks(size, cols, rows) {
  const needed = linksPerKind(size);
  const heads = headSquares(size, needed * 2);
  const taken = new Set([1, size, ...heads]);
  const ladders = {};
  const snakes = {};
  const links = [];
  let ladderCount = 0;
  let snakeCount = 0;
  let preferLadder = true;

  const covered = new Set();
  const board = { size, cols, rows, taken, covered };
  const claim = (from, to) => {
    const [fr, fc] = rcOf(from, cols);
    const [tr, tc] = rcOf(to, cols);
    pathCells(fr, fc, tr, tc).forEach((cell) => covered.add(cell));
  };
  const options = (head) =>
    ["ladder", "snake"].filter((kind) => pickDest(head, kind, board) != null);

  // Heads on the bottom row can only start a ladder and heads on the top row can
  // only start a snake. Settling those first stops the flexible middle squares
  // being handed to whichever kind happens to come first in numeric order,
  // which used to leave short boards a snake short.
  const ordered = [...heads].sort(
    (a, b) => options(a).length - options(b).length
  );

  ordered.forEach((head) => {
    const deficit = (kind) =>
      needed - (kind === "ladder" ? ladderCount : snakeCount);
    const order = preferLadder ? ["ladder", "snake"] : ["snake", "ladder"];
    // Whichever kind is furthest from its quota gets first refusal.
    order.sort((a, b) => deficit(b) - deficit(a));
    const kind = order.find(
      (candidate) =>
        deficit(candidate) > 0 && pickDest(head, candidate, board) != null
    );
    const dest = kind ? pickDest(head, kind, board) : null;
    if (dest == null) {
      // This evenly spaced square cannot host a valid climb or drop — free it.
      taken.delete(head);
      return;
    }

    taken.add(dest);
    claim(head, dest);
    if (kind === "ladder") {
      ladders[head] = dest;
      links.push({ kind, from: head, to: dest, index: ladderCount });
      ladderCount += 1;
      preferLadder = false;
    } else {
      snakes[head] = dest;
      links.push({ kind, from: head, to: dest, index: snakeCount });
      snakeCount += 1;
      preferLadder = true;
    }
  });

  // Top up either kind if the even spacing left one short (e.g. many top-row
  // heads). A crowded board may need the longer reaches to place the last one.
  const fillKind = (kind, have) => {
    for (const relaxed of [false, true]) {
      for (let square = 2; square < size && have < needed; square += 1) {
        if (taken.has(square) || ladders[square] || snakes[square]) continue;
        const dest = pickDest(square, kind, board, relaxed);
        if (dest == null) continue;
        if (kind === "ladder") ladders[square] = dest;
        else snakes[square] = dest;
        taken.add(square);
        taken.add(dest);
        claim(square, dest);
        links.push({ kind, from: square, to: dest, index: have });
        have += 1;
      }
      if (have >= needed) break;
    }
    return have;
  };

  ladderCount = fillKind("ladder", ladderCount);
  snakeCount = fillKind("snake", snakeCount);

  // A short board can run out of legal snake destinations before it runs out of
  // ladder ones. Drop the surplus so the two kinds always come in equal numbers
  // rather than leaving the board visibly lopsided.
  const keep = Math.min(ladderCount, snakeCount);
  const kept = { ladder: 0, snake: 0 };
  const balanced = [];
  links
    .sort((a, b) => a.from - b.from)
    .forEach((link) => {
      if (kept[link.kind] >= keep) {
        delete (link.kind === "ladder" ? ladders : snakes)[link.from];
        return;
      }
      balanced.push({ ...link, index: kept[link.kind] });
      kept[link.kind] += 1;
    });

  return { ladders, snakes, links: balanced };
}

/** Only START and FINISH keep cell icons — mid-board decorations clutter the links. */
function buildOrnaments(size) {
  return { 1: "start", [size]: "flag" };
}

/** Accept theme data, `{ cols, rows }`, or legacy square-count values. */
export function createBoardLayout(source) {
  const { cols, rows } =
    typeof source === "number" || typeof source === "string"
      ? resolveBoardDims({ board_size: source })
      : resolveBoardDims(source || {});
  const size = cols * rows;
  const { ladders, snakes, links } = buildLinks(size, cols, rows);
  const heads = new Set(links.map((link) => link.from));

  const visualSquares = [];
  for (let row = rows; row >= 1; row -= 1) {
    for (let col = 1; col <= cols; col += 1) {
      visualSquares.push(squareAt(row, col, cols));
    }
  }

  return {
    size,
    cols,
    rows,
    visualSquares,
    ladders,
    snakes,
    links,
    ornaments: buildOrnaments(size),
    /** Every head plus FINISH: the only squares a turn is allowed to end on. */
    stops: [...heads, size].sort((a, b) => a - b),
    rc: (square) => rcOf(square, cols),
  };
}

/** Middle of a square, in board cells, matching the artwork's SVG viewBox. */
export function squareCenter(layout, square) {
  const [row, col] = layout.rc(square);
  return { x: col - 0.5, y: layout.rows - row + 0.5 };
}

export function nextStopSquare(layout, from) {
  return layout.stops.find((square) => square > from) ?? layout.size;
}

/** Dice value that moves the player from `from` onto the next stop square. */
export function plannedRoll(layout, from) {
  return Math.max(1, Math.min(6, nextStopSquare(layout, from) - from));
}

export function squareKind(layout, square) {
  if (layout.ladders[square]) return "ladder";
  if (layout.snakes[square]) return "snake";
  return "";
}

export function squareDestination(layout, square) {
  return layout.ladders[square] ?? layout.snakes[square] ?? square;
}

export function squareLabel(layout, square) {
  if (square === 1) return "START";
  if (square === layout.size) return "FINISH";
  return String(square);
}

/** Checkerboard from old/game.php, with START and FINISH keeping colour 1. */
export function cellColor(layout, square, c1, c2, c3) {
  if (square === 1 || square === layout.size) return c1;
  const [row, col] = layout.rc(square);
  return (row + col) % 2 === 0 ? c2 : c3;
}

/** Percentages keep the token inset the same whatever the grid shape. */
export function squareTokenStyle(layout, square) {
  const [row, col] = layout.rc(square);
  const cellW = 100 / layout.cols;
  const cellH = 100 / layout.rows;
  return {
    width: `${cellW * 0.7}%`,
    left: `${(col - 1) * cellW + cellW * 0.15}%`,
    top: `${(layout.rows - row) * cellH + cellH * 0.12}%`,
  };
}
